import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import { fileService } from '../services/apiService';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { PhoneInput, normalizePhone } from './ui/phone-input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Plus, Trash2, Upload, X, CheckCircle, Loader2, ChevronLeft, ChevronRight, ImageOff, Camera, Images, Search, AlertCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { MakingStyleCard } from './estimate/MakingStyleCard';

// ─── Purity → Net-weight cascade (locked ratios, Mar 2026) ──────────────
// Anchor purity = 22K. When purity changes, net weight scales via these
// factors. Stones weight is invariant (only gold cascades). 24K not
// supported.
const PURITY_FACTORS = { '22K': 1.0, '18K': 0.85, '14K': 0.7225, '9K': 0.614125 };
const PURITY_PRIORITY = ['22K', '18K', '14K', '9K'];   // highest → lowest, used for snap-back

const base22KFromNet = (netWeight, purity) => {
  const f = PURITY_FACTORS[purity];
  if (!f || !Number.isFinite(netWeight)) return null;
  return netWeight / f;
};
const netForPurity = (base22Knet, purity) => {
  const f = PURITY_FACTORS[purity];
  if (!f || !Number.isFinite(base22Knet)) return null;
  return base22Knet * f;
};
const rateRowForPurity = (msDoc, purity) =>
  (msDoc?.rates || []).find(r => r?.purity === purity) || null;

// Post-verification grouping — collapse verified diamond rows that are
// IDENTICAL in pricing terms (same label = shape·size·colour·clarity, same
// ₹/ct, same lab basis) into ONE combined line. Totals (ct, pcs, amount) are
// summed so nothing is lost; individual entries are kept in `_members` for the
// "Contains N entries" expand. Non-diamond / manual rows pass through untouched.
const groupVerifiedDiamonds = (rows) => {
  const diamonds = [];
  const others   = [];
  (rows || []).forEach(r => {
    if (r._kind === 'diamond' && r._verified) diamonds.push(r);
    else others.push(r);
  });
  const groups = new Map();
  const order  = [];
  diamonds.forEach(r => {
    const key = `${r.name}||${r.rate}||${r._labKind || ''}`;
    if (!groups.has(key)) { groups.set(key, []); order.push(key); }
    groups.get(key).push(r);
  });
  const grouped = order.map((key, gi) => {
    const members = groups.get(key);
    if (members.length === 1) return members[0];   // nothing to merge
    const totWeight = members.reduce((s, m) => s + (Number(m.weight) || 0), 0);
    const totAmount = members.reduce((s, m) => s + (Number(m.amount) || 0), 0);
    const hasPcs    = members.some(m => m.pieces != null && m.pieces !== '');
    const totPieces = hasPcs
      ? members.reduce((s, m) => s + (Number(m.pieces) || 0), 0) : null;
    return {
      ...members[0],
      id:        `grp-${gi}-${Date.now()}`,
      weight:    Number(totWeight.toFixed(3)),
      amount:    Math.round(totAmount),
      pieces:    totPieces,
      _grouped:  true,
      _groupCount: members.length,
      _members:  members.map(m => ({
        name: m.name, weight: m.weight, pieces: m.pieces, rate: m.rate, amount: m.amount,
      })),
    };
  });
  return [...grouped, ...others];
};

const supportedPurities = (msDoc) => (msDoc?.rates || [])
  .map(r => r?.purity).filter(Boolean);

// Rule 2b — snap to the highest supported purity when target unsupported.
const snapToBestPurity = (msDoc, preferred) => {
  const list = supportedPurities(msDoc);
  if (preferred && list.includes(preferred)) return preferred;
  return PURITY_PRIORITY.find(p => list.includes(p)) || list[0] || null;
};

// Resolve charge mode + value from a rate row given the piece's net weight.
const resolveChargeFromRow = (rateRow, netWeight) => {
  if (!rateRow) return { mode: null, value: 0, threshold: null };
  const threshold = rateRow.weight_threshold_grams;
  if (threshold != null && Number.isFinite(netWeight) && netWeight <= threshold) {
    return { mode: 'flat', value: rateRow.small_piece_flat_charge || 0, threshold };
  }
  return { mode: 'per_gram', value: rateRow.large_piece_per_gram_rate || 0, threshold };
};

const EstimateFormDialog = ({
  open,
  onOpenChange,
  onSaved,
  prefill = {},
  lockCustomer = false,
  duplicateData = null,
  isStockEstimate = false,
  stockEditData = null,
}) => {
  const { toast } = useToast();
  const imageInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const [form, setForm] = useState({
    customerPhone: '', customerName: '', tagNumber: '',
    mainGroup: '', product: '', styleName: '', hsnCode: '', grossWeight: '',
    goldPurity: '22K', wastagePercentage: '8', goldRate: '',
    makingChargesType: 'per_gram', makingChargesPerGram: '', fixedMakingCharge: '',
    otherCharges: '',
  });
  const [stones, setStones] = useState([]);
  const [currentStone, setCurrentStone] = useState({ name: '', weight: '', rate: '', amount: '' });
  const [editingStoneIndex, setEditingStoneIndex] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});   // rowId → bool
  const [status, setStatus] = useState('draft');
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Verified-data prefill (DV + CSV + Making Style) — Phase D
  // Populated when the salesman clicks the "Check" button next to a tag
  // that has been fully verified. Manual-trigger only — no keystroke fire.
  const [verifiedPrefill, setVerifiedPrefill] = useState(null);
  const [verifiedLoading, setVerifiedLoading] = useState(false);
  const [verifiedError, setVerifiedError]     = useState(null);
  // Backlinks to the verified DV+CSV records when the salesman applies the
  // verified prefill. Persisted with the estimate (Phase E4 — audit trail).
  const [verifiedDvId, setVerifiedDvId] = useState(null);
  const [verifiedCsvId, setVerifiedCsvId] = useState(null);

  // Making Style card (verified-tag flow) — locks purity + drives cascade.
  const [makingStyles, setMakingStyles] = useState([]);          // all active MS docs
  const [currentMakingStyleId, setCurrentMakingStyleId] = useState(null);
  const [verifiedAnchor, setVerifiedAnchor] = useState(null);    // { base22Knet, stonesWeightGrams, msId, purity } — locked at prefill
  const [wastageOverridden, setWastageOverridden] = useState(false);
  const [makingOverridden, setMakingOverridden]   = useState(false);
  const [netWeightOverridden, setNetWeightOverridden] = useState(false);

  const [stoneAmountOverridden, setStoneAmountOverridden] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);

  // Quick Add for stone weights
  const [accumulatedWeights, setAccumulatedWeights] = useState([]);
  const [showWeightTotal, setShowWeightTotal] = useState(false);

  // Catalog
  const [mainGroups, setMainGroups] = useState([]);
  const [products, setProducts] = useState([]);
  const [goldTypes, setGoldTypes] = useState([]);
  const [todayPrices, setTodayPrices] = useState(null);

  // Calculated
  const [calc, setCalc] = useState({
    stonesWeightCarats: 0, stonesWeightGrams: 0, netWeight: 0,
    goldWeightWithWastage: 0, goldAmount: 0, stonesAmount: 0,
    makingAmount: 0, subtotal: 0, gstAmount: 0, totalAmount: 0,
  });

  useEffect(() => {
    Promise.all([
      api.get('/catalog/main-groups').catch(() => ({ data: [] })),
      api.get('/catalog/products').catch(() => ({ data: [] })),
      api.get('/catalog/gold-types').catch(() => ({ data: [] })),
      api.get('/gold-prices/latest').catch(() => ({ data: { exists: false } })),
      api.get('/making-style-master?active_only=true').catch(() => ({ data: { items: [] } })),
    ]).then(([mg, pr, gt, gp, ms]) => {
      setMainGroups(mg.data.map(i => i.name));
      setProducts(pr.data.map(i => i.name));
      setGoldTypes(gt.data.map(i => i.name));
      if (gp.data.exists) setTodayPrices(gp.data.prices);
      setMakingStyles(Array.isArray(ms.data?.items) ? ms.data.items : []);
    });
  }, []);

  // Auto-fill gold rate when today's prices load and form has purity but no rate
  useEffect(() => {
    if (todayPrices && form.goldPurity && !form.goldRate) {
      const rate = todayPrices[form.goldPurity];
      if (rate) {
        setForm(f => ({ ...f, goldRate: String(rate) }));
      }
    }
  }, [todayPrices, form.goldPurity, form.goldRate]);

  // Tag number lookup — debounced, only for customer estimates
  // Helper — tear down Making Style card state when we leave a verified
  // context (tag cleared / changed to a non-verified value / dialog closed).
  const clearVerifiedFlowState = useCallback(() => {
    setVerifiedDvId(null);
    setVerifiedCsvId(null);
    setVerifiedAnchor(null);
    setCurrentMakingStyleId(null);
    setWastageOverridden(false);
    setMakingOverridden(false);
    setNetWeightOverridden(false);
  }, []);

  // Dedicated tag-empty cleanup — fires the instant the tag input becomes
  // empty (no debounce). Separate from the tag-lookup effect so the card
  // unmounts immediately even before the debounced fetch could clear it.
  useEffect(() => {
    if (!form.tagNumber || !form.tagNumber.trim()) {
      clearVerifiedFlowState();
    }
  }, [form.tagNumber, clearVerifiedFlowState]);

  // Tag lookup is now MANUALLY triggered — the salesman clicks the "Check"
  // button (or presses Enter) after typing the full tag. We deliberately do
  // NOT fire on every keystroke: partial tags produced noise, wasted three
  // parallel HTTP calls, and made the ~30 s verified-prefill roundtrip feel
  // even slower. Verified data is the sole source of truth — the stale ERP
  // and stock-estimate lookups have been retired.
  const isTagPlausible = (form.tagNumber || '').trim().length >= 8;
  // Tag most recently fetched via Check. Only when the user edits *away* from
  // this value do we clear the banner — mere state settles from the Check
  // itself (verifiedPrefill / verifiedError changing) must NOT wipe the
  // banner they just populated.
  const lastCheckedTagRef = useRef(null);
  // Mismatch payload (HTTP 409 from prefill) — populated when ERP inventory
  // has drifted since DV/CSV. When set, the banner blocks the salesman from
  // proceeding with verified prefill; a separate manual-entry path remains.
  const [verifiedMismatch, setVerifiedMismatch] = useState(null);
  const runTagCheck = async () => {
    const tag = (form.tagNumber || '').trim();
    if (!tag) return;
    setVerifiedLoading(true);
    setVerifiedError(null);
    setVerifiedMismatch(null);
    setVerifiedPrefill(null);
    clearVerifiedFlowState();
    try {
      const { data: body } = await api.get(
        `/estimates/prefill-from-tag/${encodeURIComponent(tag)}`
      );
      lastCheckedTagRef.current = tag;
      if (body?.dv_id || body?.csv_id) {
        setVerifiedPrefill(body);
      } else {
        // Tag exists but is not verified — surface a short reason line.
        setVerifiedError(
          Array.isArray(body?.reasons) && body.reasons.length
            ? body.reasons.join(' · ')
            : 'not_verified'
        );
      }
    } catch (err) {
      lastCheckedTagRef.current = tag;
      // 409 = ERP inventory changed since verification. The response body
      // carries a structured diff (added/removed stones + parent_delta).
      if (err?.response?.status === 409 && err.response.data?.detail?.code === 'tag_re_verification_required') {
        setVerifiedMismatch(err.response.data.detail);
      } else {
        const detail = err?.response?.data?.detail;
        setVerifiedError(typeof detail === 'string' ? detail : (err?.message || 'lookup_failed'));
      }
    } finally {
      setVerifiedLoading(false);
    }
  };

  // Only clear the banner when the user edits the tag AWAY from the one we
  // last checked. Listing `verifiedPrefill` / `verifiedError` in deps would
  // create a race where the Check-triggered setState re-fires the effect
  // and wipes the banner it just populated (Feb 2026 regression fix).
  useEffect(() => {
    if (isStockEstimate) return;
    const currentTag = (form.tagNumber || '').trim();
    if (lastCheckedTagRef.current && currentTag !== lastCheckedTagRef.current) {
      lastCheckedTagRef.current = null;
      setVerifiedPrefill(null);
      setVerifiedError(null);
      setVerifiedMismatch(null);
      clearVerifiedFlowState();
    }
  }, [form.tagNumber, isStockEstimate, clearVerifiedFlowState]);

  // ── Verified-data prefill (DV + CSV + Making Style) — Phase D ────────────
  // The headline feature: when a tag is fully verified (DV done + CSV verified +
  // Making Style picked), one click prefills the *entire* estimate form with
  // the trusted data. Salesman can still edit everything (stones, making,
  // wastage) — these come pre-filled but are not locked.
  //
  // Phase F: Making Style now holds an array of per-purity rate rows. The
  // backend already resolves the row matching the tag's ERP purity into
  // `making.resolved_rate`; we use that for autofill. If `resolved_rate` is
  // null, we don't autofill wastage/making (frontend's amber warning explains
  // why and the salesman can pick rates manually).
  // Resolve an ERP-supplied catalogue value to the catalogue's canonical
  // form (case-insensitive). Returns the raw input when there's no match —
  // the form still stores it and the Select renders an inline fallback item.
  const resolveCatalogValue = useCallback((value, options) => {
    if (!value) return value;
    const lc = String(value).trim().toLowerCase();
    return options.find(o => String(o).toLowerCase() === lc) || value;
  }, []);

  const applyVerifiedPrefill = (bodyArg = null) => {
    const p = bodyArg || verifiedPrefill;
    if (!p) return;

    // ERP item context
    const erp = p.erp || {};
    const newPurity = erp.gold_purity || form.goldPurity;
    // Today's gold rate (fall back to prefill payload's gold.rate, then ERP)
    const liveRate = newPurity && todayPrices?.[newPurity]
      ? String(todayPrices[newPurity])
      : (p.gold?.rate_per_gram ? String(p.gold.rate_per_gram) : form.goldRate);

    // Resolved per-purity rate row from the Making Style (Phase F).
    // Resolved mode + value account for the weight-bracket (Phase G):
    //   • flat       → small piece (net_weight ≤ threshold) → flat ₹
    //   • per_gram   → large piece (net_weight > threshold) → ₹/gm
    const ms = p.making || {};
    const rate = ms.resolved_rate || null;
    const mode = ms.resolved_charge_mode;     // 'flat' | 'per_gram' | null
    const value = ms.resolved_charge_value;   // number | null

    // Bug fix — ERP may return values in different casing ("GOLD" vs catalog
    // "Gold") or values that don't exist in the catalog at all (e.g.
    // "Gold SPARES"). Normalise to catalogue's canonical form when we can,
    // else pass the raw value through — the Select below renders an inline
    // fallback option so the user actually sees it selected.
    const resolvedMainGroup = resolveCatalogValue(erp.main_group, mainGroups);
    const resolvedProduct   = resolveCatalogValue(erp.sub_group,  products);

    setForm(f => ({
      ...f,
      // Item identity (Design Style + HSN come from ERP)
      mainGroup: resolvedMainGroup || f.mainGroup,
      product:   resolvedProduct   || f.product,
      styleName: erp.design_style_name || f.styleName,
      hsnCode:   erp.hsn_code || f.hsnCode,
      grossWeight: erp.gross_weight != null ? String(erp.gross_weight) : f.grossWeight,
      goldPurity:  newPurity,
      goldRate:    liveRate,
      // Wastage from the matched purity row
      wastagePercentage: rate?.wastage_percentage != null
        ? String(rate.wastage_percentage) : f.wastagePercentage,
      // Net-weight-aware making mode. The Making Style master returns two
      // modes (Phase G):
      //   • 'per_gram'  → large piece (net_wt > threshold) → ₹/g
      //   • 'flat'      → small piece (net_wt ≤ threshold) → flat ₹
      // Both 'flat' and 'fixed' (manual override) bill identically — a
      // single ₹ amount with no net-weight multiplication — so they share
      // the same backend field (`fixed_making_charge`) and the same input
      // box on the form.
      makingChargesType:
        mode === 'flat'     ? 'flat'
      : mode === 'per_gram' ? 'per_gram'
      : f.makingChargesType,
      makingChargesPerGram:
        mode === 'per_gram' && value != null ? String(value) : f.makingChargesPerGram,
      fixedMakingCharge:
        mode === 'flat'     && value != null ? String(value) : f.fixedMakingCharge,
    }));

    // Stones = diamonds (DV-resolved) + colour stones (CSV-frozen)
    const stoneRows = [];
    (p.diamonds || []).forEach((d, i) => {
      const ct      = Number(d.weight_cts || 0);
      const pricing = d.pricing || {};
      // Phase E2 — resolver result drives the diamond row's ₹/ct.
      //   • rate_source === "grid"             → use final_rate
      //   • "manual_required" / "awaiting_index" → leave 0; salesman fills
      const fromGrid = pricing.rate_source === 'grid';
      const dRate    = fromGrid && pricing.final_rate ? Number(pricing.final_rate) : 0;
      const dAmount  = dRate ? Math.round(ct * dRate) : 0;
      const labelBits = [];
      if (d.shape) labelBits.push(d.shape);
      if (d.size) labelBits.push(d.size);
      if ((d.colors || []).length) labelBits.push(d.colors.join('/'));
      // Clarity: fold granular cert grades (VS1/VS2/SI1/SI2) back to the
      // pricing grid buckets (VS/SI) so the row reads as the pricing basis.
      // Raw cert grades stay in d.clarities for audit / cert reproduction.
      if ((d.clarities || []).length) {
        const buckets = [];
        (d.clarities || []).forEach((c) => {
          const b = String(c || '').toUpperCase().replace(/\d+$/, '');
          if (b && !buckets.includes(b)) buckets.push(b);
        });
        if (buckets.length) labelBits.push(buckets.join('/'));
      }
      // Lab pricing basis is rendered as a coloured chip next to the row —
      // no longer folded into the label string. Store enough context to
      // pick the chip variant:
      //   • certified — external lab (GIA/IGI/HRD/SGL) with a grid rate
      //   • in_house  — TDS grid rate (in-house verification)
      //   • manual    — no grid rate → salesman must fill (any lab)
      let labKind = null;
      if (pricing.lab_label) {
        if (!fromGrid) labKind = 'manual';
        else if (pricing.lab_label === 'TDS') labKind = 'in_house';
        else labKind = 'certified';
      }
      stoneRows.push({
        id: Date.now() + i,
        name:    labelBits.join(' · ') || 'Diamond',
        weight:  ct,
        rate:    dRate,
        amount:  dAmount,
        pieces:  d.pieces,
        _verified: true,
        _kind:     'diamond',
        _source:   d.source,
        _pricing:  pricing,   // full debug payload — tooltip-ready
        _labChip:  pricing.lab_label || null,
        _labKind:  labKind,   // 'certified' | 'in_house' | 'manual' | null
      });
    });
    (p.colour_stones || []).forEach((s, i) => {
      const ct = Number(s.weight_ct || 0);
      const rate = Number(s.rate_per_carat || 0);
      // Phrasing matches the Gemstone Master combination card:
      //   {stone_name} · {family} · {origin} · {form} [· variant]
      const labelBits = [];
      if (s.stone_name) labelBits.push(s.stone_name);
      if (s.family)     labelBits.push(s.family);
      if (s.origin)     labelBits.push(s.origin);
      if (s.form)       labelBits.push(s.form);
      if (s.variant)    labelBits.push(s.variant);
      stoneRows.push({
        id: Date.now() + 1000 + i,
        name: labelBits.join(' · ') || (s.family || 'Colour stone'),
        weight: ct,
        rate: rate,
        amount: Math.round(ct * rate),
        pieces: s.pieces,
        _verified: true,
        _kind: 'colour_stone',
        _premium: s.premium_price != null,
      });
    });
    if (stoneRows.length) setStones(groupVerifiedDiamonds(stoneRows));

    // Use the verified DV photo as the first image if no images yet.
    if (p.photo_url && !images.length) {
      setImages([{ url: p.photo_url, name: 'Verified product photo' }]);
    }

    // ── Making Style card state — lock the 22K anchor once, so subsequent
    // purity swaps derive from the ORIGINAL verified net weight (no drift
    // from repeated flips). Stones weight is invariant (only gold cascades).
    // Anchor uses ERP's `net_weight` (the pure-gold portion) so the user's
    // business rule ("22K net 100g → 18K net 85g") applies correctly.
    const stonesWeightGrams =
        (p.diamonds || []).reduce((sum, d) => sum + (Number(d.weight_cts) || 0) * 0.2, 0)
      + (p.colour_stones || []).reduce((sum, s) => sum + (Number(s.weight_ct) || 0) * 0.2, 0);
    const originalNet = erp.net_weight != null ? Number(erp.net_weight) : null;
    const anchor22K   = originalNet != null && newPurity
      ? base22KFromNet(originalNet, newPurity) : null;
    if (anchor22K != null) {
      setVerifiedAnchor({
        base22Knet:       anchor22K,
        stonesWeightGrams,
        purity:           newPurity,
        netWeight:        originalNet,
        msId:             ms.making_style_id || null,
        msName:           ms.making_style_name || null,
        // Baseline snapshot — used by resetToVerified() for a synchronous
        // local restore (no API round-trip, no race condition).
        baselineWastage:      rate?.wastage_percentage != null ? String(rate.wastage_percentage) : '',
        baselineMode:         mode || 'per_gram',
        baselinePerGram:      mode === 'per_gram' && value != null ? String(value) : '',
        baselineFixed:        mode === 'flat'     && value != null ? String(value) : '',
        baselineGrossWeight:  (originalNet + stonesWeightGrams).toFixed(3),
        baselineGoldRate:     liveRate,
      });
      setCurrentMakingStyleId(ms.making_style_id || null);
      // Align gross_weight so calc.netWeight (=gross-stones_grams) exactly
      // equals the anchor net weight from t=0 (no cosmetic drift between
      // ms-card-netwt and ms-card-anchor).
      setForm(f => ({
        ...f,
        grossWeight: (originalNet + stonesWeightGrams).toFixed(3),
      }));
    }
    // Fresh cascade = clean override slate.
    setWastageOverridden(false);
    setMakingOverridden(false);
    setNetWeightOverridden(false);

    // Hide all banners — verified data wins.
    setVerifiedDvId(p.dv_id || null);
    setVerifiedCsvId(p.csv_id || null);
    setVerifiedPrefill(null);
    setVerifiedError(null);
  };

  // ── Making Style dropdown cascade ────────────────────────────────────
  // Picking a new Making Style (or accepting the verified one) → snaps
  // purity to a supported grade, re-derives net weight via the 22K anchor,
  // and rewrites wastage + making from the new rate row. Any manual
  // overrides are cleared because a new MS = new source of truth.
  const applyMakingStyleCascade = (msDoc, preferredPurity = null) => {
    if (!msDoc || !verifiedAnchor) return;
    const targetPurity = snapToBestPurity(msDoc, preferredPurity || form.goldPurity);
    if (!targetPurity) {
      toast({ title: 'Making Style unusable', description: 'No supported purity rows found.', variant: 'destructive' });
      return;
    }
    const rateRow  = rateRowForPurity(msDoc, targetPurity);
    const newNet   = netForPurity(verifiedAnchor.base22Knet, targetPurity);
    const newGross = newNet != null ? newNet + verifiedAnchor.stonesWeightGrams : null;
    const { mode, value } = resolveChargeFromRow(rateRow, newNet);
    const liveGoldRate = todayPrices?.[targetPurity];

    setForm(f => ({
      ...f,
      goldPurity:  targetPurity,
      goldRate:    liveGoldRate != null ? String(liveGoldRate) : f.goldRate,
      grossWeight: newGross != null ? newGross.toFixed(3) : f.grossWeight,
      wastagePercentage:    rateRow?.wastage_percentage != null ? String(rateRow.wastage_percentage) : f.wastagePercentage,
      makingChargesType:    mode || f.makingChargesType,
      makingChargesPerGram: mode === 'per_gram' ? String(value) : f.makingChargesPerGram,
      fixedMakingCharge:    mode === 'flat'     ? String(value) : f.fixedMakingCharge,
    }));
    setCurrentMakingStyleId(msDoc.id || msDoc._id || null);
    setWastageOverridden(false);
    setMakingOverridden(false);
    setNetWeightOverridden(false);
  };

  const resetToVerified = () => {
    // Purely local reset — no API round-trip. Uses the baseline values
    // snapshotted into `verifiedAnchor` at initial apply, so nothing can
    // race or drift.
    if (!verifiedAnchor) return;
    setForm(f => ({
      ...f,
      goldPurity:           verifiedAnchor.purity,
      goldRate:             verifiedAnchor.baselineGoldRate || f.goldRate,
      grossWeight:          verifiedAnchor.baselineGrossWeight || f.grossWeight,
      wastagePercentage:    verifiedAnchor.baselineWastage    || f.wastagePercentage,
      makingChargesType:    verifiedAnchor.baselineMode        || f.makingChargesType,
      makingChargesPerGram: verifiedAnchor.baselineMode === 'per_gram'
        ? (verifiedAnchor.baselinePerGram || '') : f.makingChargesPerGram,
      fixedMakingCharge:    verifiedAnchor.baselineMode === 'flat'
        ? (verifiedAnchor.baselineFixed || '') : f.fixedMakingCharge,
    }));
    setCurrentMakingStyleId(verifiedAnchor.msId);
    setWastageOverridden(false);
    setMakingOverridden(false);
    setNetWeightOverridden(false);
  };

  const currentMakingStyle = makingStyles.find(m => (m.id || m._id) === currentMakingStyleId) || null;
  const isVerifiedFlow     = Boolean(verifiedDvId || verifiedCsvId);

  // Track previous open state to only reset on open transition
  const prevOpenRef = useRef(false);
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;

    // Only reset form when transitioning from closed → open
    if (open && !wasOpen) {
      setVerifiedPrefill(null);
      setVerifiedLoading(false);
      setVerifiedError(null);
      const sourceData = duplicateData || stockEditData;
      if (sourceData) {
        setForm({
          customerPhone: sourceData.customer_phone || prefill.customerPhone || '',
          customerName: sourceData.customer_name || prefill.customerName || '',
          tagNumber: sourceData.tag_number || '',
          mainGroup: sourceData.main_group || '',
          product: sourceData.product || '',
          styleName: sourceData.style_name || '',
          hsnCode: sourceData.hsn_code || '',
          grossWeight: sourceData.gross_weight?.toString() || '',
          goldPurity: sourceData.gold_purity || '22K',
          wastagePercentage: sourceData.wastage_percentage?.toString() || '8',
          goldRate: sourceData.gold_rate?.toString() || '',
          makingChargesType: sourceData.making_charges_type || 'per_gram',
          makingChargesPerGram: sourceData.making_charges_per_gram?.toString() || '',
          fixedMakingCharge: sourceData.fixed_making_charge?.toString() || '',
          otherCharges: sourceData.other_charges?.toString() || '',
        });
        setStones((sourceData.stones || []).map((s, i) => ({ ...s, id: Date.now() + i })));
        setStatus(sourceData.status || 'draft');
        setImages((sourceData.images || []).map((url, i) => ({ url, name: `Photo ${i + 1}` })));
      } else {
        setForm({
          customerPhone: prefill.customerPhone || '',
          customerName: prefill.customerName || '',
          tagNumber: '', mainGroup: prefill.mainGroup || '', product: prefill.product || '',
          styleName: '', hsnCode: '',
          grossWeight: '', goldPurity: '22K', wastagePercentage: '8', goldRate: '',
          makingChargesType: 'per_gram', makingChargesPerGram: '', fixedMakingCharge: '', otherCharges: '',
        });
        setStones([]);
        setStatus('draft');
        setImages([]);
      }
      setVerifiedDvId(null);
      setVerifiedCsvId(null);
      setCurrentStone({ name: '', weight: '', rate: '', amount: '' });
      setEditingStoneIndex(null);
      setAccumulatedWeights([]);
      setShowWeightTotal(false);
      setStoneAmountOverridden(false);
      setWizardStep(1);
    }
  }, [open, prefill, duplicateData, stockEditData]);

  // Recalculate
  useEffect(() => {
    const grossWeight = parseFloat(form.grossWeight) || 0;
    const wastage = parseFloat(form.wastagePercentage) || 0;
    const goldRate = parseFloat(form.goldRate) || 0;
    const otherCharges = parseFloat(form.otherCharges) || 0;

    const effectiveStones = stones.map((s, idx) => {
      if (idx === editingStoneIndex) {
        const w = parseFloat(currentStone.weight) || 0;
        const r = parseFloat(currentStone.rate) || 0;
        const a = currentStone.amount ? parseFloat(currentStone.amount) : w * r;
        return { ...s, weight: w, rate: r, amount: a };
      }
      return s;
    });

    const stonesWeightCarats = effectiveStones.reduce((sum, s) => sum + (parseFloat(s.weight) || 0), 0);
    const stonesWeightGrams = stonesWeightCarats * 0.2;
    const netWeight = grossWeight - stonesWeightGrams;
    const goldWeightWithWastage = netWeight * (1 + wastage / 100);
    const goldAmount = Math.round(goldWeightWithWastage * goldRate);
    const stonesAmount = Math.round(effectiveStones.reduce((sum, s) => {
      const a = parseFloat(s.amount);
      return sum + (isNaN(a) ? (parseFloat(s.weight) || 0) * (parseFloat(s.rate) || 0) : a);
    }, 0));

    let makingAmount = 0;
    if (form.makingChargesType === 'per_gram') {
      makingAmount = Math.round(netWeight * (parseFloat(form.makingChargesPerGram) || 0));
    } else {
      makingAmount = Math.round(parseFloat(form.fixedMakingCharge) || 0);
    }

    const subtotal = goldAmount + stonesAmount + makingAmount + Math.round(otherCharges);
    const gstAmount = Math.round(subtotal * 0.03);
    const totalAmount = subtotal + gstAmount;

    setCalc({ stonesWeightCarats, stonesWeightGrams, netWeight, goldWeightWithWastage, goldAmount, stonesAmount, makingAmount, subtotal, gstAmount, totalAmount });
  }, [form, stones, currentStone, editingStoneIndex]);

  const fmt = (n) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;

  // Stone Quick Add
  const handleQuickAdd = () => {
    const w = parseFloat(currentStone.weight) || 0;
    if (w <= 0) return;
    setAccumulatedWeights(prev => [...prev, w]);
    setShowWeightTotal(true);
    setCurrentStone(s => ({ ...s, weight: '' }));
  };

  const handleUseTotal = () => {
    const total = accumulatedWeights.reduce((sum, w) => sum + w, 0);
    setCurrentStone(s => ({ ...s, weight: total.toFixed(2) }));
    setShowWeightTotal(false);
    setAccumulatedWeights([]);
  };

  const handleClearWeights = () => {
    setAccumulatedWeights([]);
    setShowWeightTotal(false);
  };

  const handleAddStone = () => {
    const w = parseFloat(currentStone.weight) || 0;
    const r = parseFloat(currentStone.rate) || 0;
    // Whole-rupee lock (Feb-2026): store amount as int rupees regardless of
    // whether it came from manual override or w × r auto-calc.
    const a = Math.round(stoneAmountOverridden && currentStone.amount ? parseFloat(currentStone.amount) : w * r);
    if (!currentStone.name && w === 0) return;

    if (editingStoneIndex !== null) {
      const updated = [...stones];
      updated[editingStoneIndex] = { ...updated[editingStoneIndex], name: currentStone.name, weight: w, rate: r, amount: a };
      setStones(updated);
      setEditingStoneIndex(null);
    } else {
      setStones([...stones, { name: currentStone.name, weight: w, rate: r, amount: a, id: Date.now() }]);
    }
    setCurrentStone({ name: '', weight: '', rate: '', amount: '' });
    setAccumulatedWeights([]);
    setShowWeightTotal(false);
    setStoneAmountOverridden(false);
  };

  const handleEditStone = (idx) => {
    const s = stones[idx];
    setCurrentStone({ name: s.name || '', weight: s.weight?.toString() || '', rate: s.rate?.toString() || '', amount: s.amount?.toString() || '' });
    setEditingStoneIndex(idx);
    // Check if amount was manually overridden (differs from weight × rate)
    const calcAmt = Math.round((parseFloat(s.weight) || 0) * (parseFloat(s.rate) || 0));
    setStoneAmountOverridden(Math.abs((parseFloat(s.amount) || 0) - calcAmt) > 1);
  };

  const handleCancelEdit = () => {
    setEditingStoneIndex(null);
    setCurrentStone({ name: '', weight: '', rate: '', amount: '' });
    setStoneAmountOverridden(false);
  };

  const handleRemoveStone = (idx) => {
    setStones(stones.filter((_, i) => i !== idx));
    if (editingStoneIndex === idx) handleCancelEdit();
  };

  const handleSave = async () => {
    if (!isStockEstimate && (!form.customerPhone || !form.customerName)) {
      toast({ title: 'Customer phone and name required', variant: 'destructive' }); return;
    }
    if (isStockEstimate && !form.tagNumber) {
      toast({ title: 'Tag number required', variant: 'destructive' }); return;
    }
    if (!form.grossWeight || !form.goldRate) {
      toast({ title: 'Gross weight and gold rate required', variant: 'destructive' }); return;
    }
    setSaving(true);
    try {
      if (isStockEstimate) {
        const payload = {
          tag_number: form.tagNumber, main_group: form.mainGroup, product: form.product,
          gross_weight: parseFloat(form.grossWeight), gold_purity: form.goldPurity,
          wastage_percentage: parseFloat(form.wastagePercentage),
          stones: stones.map(s => ({
            name: s.name || 'Stone',
            weight: s.weight,
            rate: s.rate,
            amount: s.amount,
            ...(s.pieces != null && s.pieces !== '' ? { pieces: Number(s.pieces) || 0 } : {}),
          })),
          making_charges_type: form.makingChargesType,
          making_charges_per_gram: parseFloat(form.makingChargesPerGram) || 0,
          fixed_making_charge: parseFloat(form.fixedMakingCharge) || 0,
          other_charges: parseFloat(form.otherCharges) || 0,
          images: images.map(i => i.url),
          notes: '',
        };
        let res;
        if (stockEditData?._id) {
          res = await api.put(`/stock-estimates/${stockEditData._id}`, payload);
        } else {
          res = await api.post('/stock-estimates', payload);
        }
        toast({ title: stockEditData?._id ? 'Stock estimate updated' : 'Stock estimate created', description: res.data.estimate_number || '' });
      } else {
        const payload = {
          customer_phone: form.customerPhone, customer_name: form.customerName,
          tag_number: form.tagNumber || null, main_group: form.mainGroup, product: form.product,
          style_name: form.styleName || null, hsn_code: form.hsnCode || null,
          gross_weight: parseFloat(form.grossWeight), gold_purity: form.goldPurity,
          wastage_percentage: parseFloat(form.wastagePercentage),
          gold_rate: parseFloat(form.goldRate),
          stones: stones.map(s => ({
            name: s.name || 'Stone',
            weight: s.weight,
            rate: s.rate,
            amount: s.amount,
            ...(s.pieces != null && s.pieces !== '' ? { pieces: Number(s.pieces) || 0 } : {}),
            ...(s._verified  ? { from_verified: true } : {}),
            ...(s._kind      ? { kind: s._kind } : {}),
            ...(s._source    ? { source: s._source } : {}),
            ...(s._pricing && s._pricing.lab_label
                              ? { lab_label: s._pricing.lab_label } : {}),
            ...(s._pricing && s._pricing.base_rate != null
                              ? { base_rate: s._pricing.base_rate } : {}),
          })),
          making_charges_type: form.makingChargesType,
          making_charges_per_gram: parseFloat(form.makingChargesPerGram) || 0,
          fixed_making_charge: parseFloat(form.fixedMakingCharge) || 0,
          other_charges: parseFloat(form.otherCharges) || 0,
          status, images: images.map(i => i.url),
          // Phase E4 — verified backlinks (audit trail)
          ...(verifiedDvId  ? { dv_id:  verifiedDvId  } : {}),
          ...(verifiedCsvId ? { csv_id: verifiedCsvId } : {}),
          // Making Style card audit flags — only meaningful for verified flows
          ...(isVerifiedFlow ? {
            wastage_overridden: wastageOverridden,
            making_overridden:  makingOverridden,
          } : {}),
        };
        const res = await api.post('/estimates', payload);
        toast({ title: 'Estimate created', description: res.data.estimate_number });
        onOpenChange(false);
        if (onSaved) onSaved(res.data);
        return;
      }
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (error) {
      const msg = typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Failed to create estimate';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const stoneCalcAmount = Math.round((parseFloat(currentStone.weight) || 0) * (parseFloat(currentStone.rate) || 0));
  const stoneDisplayAmount = stoneAmountOverridden ? currentStone.amount : (stoneCalcAmount > 0 ? String(stoneCalcAmount) : '');

  const goldPurityOptions = goldTypes.length > 0 ? goldTypes : ['9K', '14K', '18K', '22K', '24K'];

  // Bug fix — when the form value comes from ERP and isn't in the catalogue
  // (e.g. "Gold SPARES" / "GOLD"), inject it as a fallback option so the
  // shadcn `<Select>` actually displays it as selected instead of going blank.
  const mainGroupOptions = form.mainGroup && !mainGroups.includes(form.mainGroup)
    ? [...mainGroups, form.mainGroup]
    : mainGroups;
  const productOptions = form.product && !products.includes(form.product)
    ? [...products, form.product]
    : products;

  // For stock estimates keep old scroll layout; for customer estimates use wizard
  if (isStockEstimate) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[95vh] overflow-y-auto" data-testid="estimate-form-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl">{stockEditData?._id ? 'Edit Stock Estimate' : 'New Stock Estimate'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <div className="space-y-2">
              <Label className="text-base">Tag Number *</Label>
              <Input type="text" inputMode="numeric" value={form.tagNumber} onChange={e => setForm(f => ({ ...f, tagNumber: e.target.value }))} className="text-base py-6" placeholder="Tag number (required)" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2"><Label className="text-base">Main Group</Label>
                <Select value={form.mainGroup} onValueChange={v => setForm(f => ({ ...f, mainGroup: v }))}><SelectTrigger className="text-base py-6" data-testid="stock-main-group-trigger"><SelectValue placeholder="Select group" /></SelectTrigger><SelectContent>{mainGroupOptions.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="space-y-2"><Label className="text-base">Product</Label>
                <Select value={form.product} onValueChange={v => setForm(f => ({ ...f, product: v }))}><SelectTrigger className="text-base py-6" data-testid="stock-product-trigger"><SelectValue placeholder="Select product" /></SelectTrigger><SelectContent>{productOptions.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            {/* Style row — sourced from ERP `Com_Style_Name`. */}
            <div className="space-y-2"><Label className="text-base">Style</Label>
              <Input value={form.styleName} onChange={e => setForm(f => ({ ...f, styleName: e.target.value }))} placeholder="Design / style name from ERP" className="text-base py-6" data-testid="stock-style-input" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2"><Label className="text-base">Gross Weight (grams) *</Label>
                <Input type="number" step="0.01" inputMode="decimal" value={form.grossWeight} onChange={e => setForm(f => ({ ...f, grossWeight: e.target.value }))} className="text-base py-6" /></div>
              <div className="space-y-2"><Label className="text-base">Gold Purity *</Label>
                <div className="flex flex-wrap gap-2">{goldPurityOptions.map(p => <button key={p} type="button" onClick={() => { const rate = todayPrices?.[p]; setForm(f => ({ ...f, goldPurity: p, goldRate: rate ? String(rate) : f.goldRate })); }} className={`px-4 py-3 rounded-lg text-sm font-semibold border-2 transition-all ${form.goldPurity === p ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white border-gray-300'}`}>{p}</button>)}</div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2"><Label className="text-base">Wastage %</Label><Input type="number" step="0.1" inputMode="decimal" value={form.wastagePercentage} onChange={e => setForm(f => ({ ...f, wastagePercentage: e.target.value }))} className="text-base py-6" /></div>
              <div className="space-y-2"><Label className="text-base">Gold Rate (₹/gram) *</Label><Input type="number" inputMode="numeric" value={form.goldRate} onChange={e => setForm(f => ({ ...f, goldRate: e.target.value }))} className="text-base py-6" /></div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="text-base py-6 px-6">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-base py-6 px-8" data-testid="save-estimate-btn">
              {saving ? 'Saving...' : stockEditData?._id ? 'Update Stock Estimate' : 'Save Stock Estimate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // ── Customer Estimate — 4-Step Wizard ──────────────────────────────────────
  const stepTitles = ['Customer & Item', 'Gold Details', 'Stones', 'Charges & Review'];

  const handleWizardNext = () => {
    if (wizardStep === 1) {
      if (!form.customerPhone || !form.customerName) {
        toast({ title: 'Required', description: 'Please enter customer phone and name', variant: 'destructive' });
        return;
      }
    }
    if (wizardStep === 2) {
      if (!form.grossWeight || !form.goldRate) {
        toast({ title: 'Required', description: 'Please enter gross weight and gold rate', variant: 'destructive' });
        return;
      }
    }
    setWizardStep(s => s + 1);
  };

  const renderStep1 = () => (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-base">Customer Phone *</Label>
          <PhoneInput value={normalizePhone(form.customerPhone)} onChange={val => setForm(f => ({ ...f, customerPhone: val }))} disabled={lockCustomer} inputProps={{ 'data-testid': 'est-phone' }} />
        </div>
        <div className="space-y-2">
          <Label className="text-base">Customer Name *</Label>
          <Input value={form.customerName} onChange={e => setForm(f => ({ ...f, customerName: e.target.value }))} disabled={lockCustomer} className="text-base py-6" data-testid="est-name" />
        </div>
      </div>
      <div className="space-y-2">
        <Label className="text-base">Tag Number</Label>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Input
              type="text"
              inputMode="numeric"
              value={form.tagNumber}
              onChange={e => setForm(f => ({ ...f, tagNumber: e.target.value }))}
              onKeyDown={e => {
                if (e.key === 'Enter' && isTagPlausible && !verifiedLoading) {
                  e.preventDefault();
                  runTagCheck();
                }
              }}
              className="text-base py-6"
              placeholder="Tag/Reference number (optional)"
              data-testid="estimate-tag-number-input"
            />
            {verifiedLoading && (
              <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400" />
            )}
          </div>
          <Button
            type="button"
            onClick={runTagCheck}
            disabled={!isTagPlausible || verifiedLoading}
            className="h-[52px] px-5 bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-40"
            data-testid="estimate-tag-check-btn"
            title={!isTagPlausible ? 'Enter the full tag number (≥ 8 digits)' : 'Fetch verified data for this tag'}
          >
            {verifiedLoading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking…</>
            ) : (
              <><Search className="w-4 h-4 mr-2" /> Check</>
            )}
          </Button>
        </div>
        {verifiedError && !verifiedPrefill && !verifiedMismatch && (
          <div
            className="flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
            data-testid="estimate-tag-not-verified"
          >
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>Tag not verified — proceed with manual entry. Reason: <span className="font-mono">{verifiedError}</span></span>
          </div>
        )}
        {verifiedMismatch && (
          <div
            className="flex items-start gap-2 text-sm text-red-900 bg-red-50 border-2 border-red-300 rounded-lg px-3 py-3"
            data-testid="estimate-tag-mismatch-banner"
          >
            <AlertCircle className="w-5 h-5 flex-shrink-0 text-red-600 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-red-800 mb-1">
                Tag modified at factory after verification
              </div>
              <div className="text-xs text-red-700 mb-1.5">
                Send this tag back to HO for fresh DV/CSV before creating an estimate.
              </div>
              {(verifiedMismatch.added?.length || verifiedMismatch.removed?.length) ? (
                <div className="text-[11px] text-red-800 space-y-0.5">
                  {verifiedMismatch.added?.length ? (
                    <div><strong>Added:</strong> {verifiedMismatch.added.length} stone row(s)</div>
                  ) : null}
                  {verifiedMismatch.removed?.length ? (
                    <div><strong>Removed:</strong> {verifiedMismatch.removed.length} stone row(s)</div>
                  ) : null}
                </div>
              ) : null}
              {verifiedMismatch.parent_delta && Object.keys(verifiedMismatch.parent_delta).length > 0 && (
                <div className="text-[11px] text-red-800 mt-1">
                  <strong>Parent changes:</strong>{' '}
                  {Object.entries(verifiedMismatch.parent_delta).map(([k, [oldV, newV]]) =>
                    `${k}: ${oldV} → ${newV}`).join(' · ')}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Making Style Card (verified-tag flow only) ────────────────────
            Standing context, not a dismissible banner. Salesman picks a
            Making Style from the dropdown → purity, weight, wastage, and
            making charge all cascade. Purity radios in Step 2 are locked
            for verified tags — the only pathway to change purity is via
            this dropdown. */}
        {isVerifiedFlow && verifiedAnchor && (
          <MakingStyleCard
            makingStyles={makingStyles}
            currentMakingStyle={currentMakingStyle}
            currentMakingStyleId={currentMakingStyleId}
            verifiedAnchor={verifiedAnchor}
            form={form}
            setForm={setForm}
            calcNetWeight={calc.netWeight}
            wastageOverridden={wastageOverridden}
            makingOverridden={makingOverridden}
            netWeightOverridden={netWeightOverridden}
            setWastageOverridden={setWastageOverridden}
            setMakingOverridden={setMakingOverridden}
            onSelectMakingStyle={(doc) => applyMakingStyleCascade(doc)}
            onSelectPurityChip={(p) => { if (currentMakingStyle) applyMakingStyleCascade(currentMakingStyle, p); }}
            onReset={resetToVerified}
            toast={toast}
          />
        )}

        {verifiedPrefill && verifiedPrefill.eligible && (
          <div className="flex items-start justify-between bg-emerald-50 border-2 border-emerald-400 rounded-lg px-3 py-2.5 gap-2 shadow-sm" data-testid="verified-prefill-banner">
            <div className="text-sm text-emerald-900 min-w-0 flex-1">
              <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                <span className="font-bold text-emerald-700">✓ Verified data ready</span>
                {verifiedPrefill.interim_tds && (
                  <span className="text-[10px] bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded font-medium" data-testid="est-tds-interim-chip">
                    Priced at TDS · Lab pending
                  </span>
                )}
                {verifiedPrefill.making?.making_style_name && (
                  <span className="text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded font-medium" data-testid="verified-making-style-chip">
                    {verifiedPrefill.making.making_style_name}
                  </span>
                )}
                {verifiedPrefill.making?.resolved_purity && (
                  <span className="text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded font-medium" data-testid="verified-purity-chip">
                    {verifiedPrefill.making.resolved_purity}
                  </span>
                )}
                {verifiedPrefill.erp?.design_style_name && (
                  <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium">
                    Design: {verifiedPrefill.erp.design_style_name}
                  </span>
                )}
              </div>
              <span className="text-xs text-emerald-800">
                {verifiedPrefill.diamonds?.length || 0} diamond row(s) · {verifiedPrefill.colour_stones?.length || 0} colour-stone row(s)
                {verifiedPrefill.making?.resolved_rate?.wastage_percentage != null
                  && ` · Wastage ${verifiedPrefill.making.resolved_rate.wastage_percentage}%`}
                {verifiedPrefill.making?.resolved_charge_mode === 'flat'
                  && verifiedPrefill.making?.resolved_charge_value != null
                  && ` · MC ₹${verifiedPrefill.making.resolved_charge_value.toLocaleString('en-IN')} flat (≤${verifiedPrefill.making.resolved_rate.weight_threshold_grams}g)`}
                {verifiedPrefill.making?.resolved_charge_mode === 'per_gram'
                  && verifiedPrefill.making?.resolved_charge_value != null
                  && ` · MC ₹${verifiedPrefill.making.resolved_charge_value}/g`}
              </span>
            </div>
            <Button type="button" size="sm" onClick={() => applyVerifiedPrefill()}
              className="flex-shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-7 px-3"
              data-testid="use-verified-data-btn">
              <CheckCircle className="w-3 h-3 mr-1" /> Use Verified Data
            </Button>
          </div>
        )}
        {verifiedPrefill && !verifiedPrefill.eligible && (
          <div className="bg-amber-50 border border-amber-300 rounded-lg px-3 py-2 text-xs text-amber-800" data-testid="verified-prefill-warning">
            <div className="font-bold mb-0.5">⚠ Tag found but not fully verified</div>
            <div className="text-[11px] text-amber-700">
              Missing: {(verifiedPrefill.reasons || []).map(r => ({
                'not_in_success_branch':     'tag not in Success branch',
                'dv_not_found':              'Diamond Verification',
                'dv_draft':                  'DV still in draft',
                'dv_in_lab':                 'DV awaiting lab results',
                'csv_not_created':           'Colour Stone Verification',
                'csv_not_verified':          'CSV still in draft',
                'making_style_missing':      'Making Style on CSV',
                'purity_not_in_making_style':
                  `Making Style "${verifiedPrefill.making?.making_style_name || ''}" doesn't have rates for ${verifiedPrefill.making?.resolved_purity || 'this purity'} (supports: ${(verifiedPrefill.making?.supported_purities || []).join(', ') || 'none'})`,
                'erp_unreachable':           'ERP unreachable',
              }[r] || r)).join('; ')}.
              You can still proceed with manual entry below.
            </div>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2"><Label className="text-base">Main Group</Label>
          <Select value={form.mainGroup} onValueChange={v => setForm(f => ({ ...f, mainGroup: v }))}><SelectTrigger className="text-base py-6" data-testid="est-main-group-trigger"><SelectValue placeholder="Select group" /></SelectTrigger><SelectContent>{mainGroupOptions.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent></Select>
        </div>
        <div className="space-y-2"><Label className="text-base">Product</Label>
          <Select value={form.product} onValueChange={v => setForm(f => ({ ...f, product: v }))}><SelectTrigger className="text-base py-6" data-testid="est-product-trigger"><SelectValue placeholder="Select product" /></SelectTrigger><SelectContent>{productOptions.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
        </div>
        {/* Style row — sourced from ERP `Com_Style_Name`. Surfaces the actual
            jewellery design (e.g. "DIA TRADITIONAL JHUMKA") which is more
            specific than Sub Group ("DIA EARINGS"). Editable freeform. */}
        <div className="space-y-2 sm:col-span-2"><Label className="text-base">Style</Label>
          <Input value={form.styleName} onChange={e => setForm(f => ({ ...f, styleName: e.target.value }))} placeholder="Design / style name from ERP" className="text-base py-6" data-testid="est-style-input" />
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-base">Gross Weight (grams) *</Label>
          <Input type="number" step="0.01" inputMode="decimal" value={form.grossWeight} onChange={e => setForm(f => ({ ...f, grossWeight: e.target.value }))} className="text-base py-6" data-testid="est-gross-weight" />
        </div>
        <div className="space-y-2">
          <Label className="text-base">Gold Purity *</Label>
          <div className="flex flex-wrap gap-2">
            {goldPurityOptions.map(p => {
              const disabled = isVerifiedFlow;   // verified → change purity only via MS dropdown
              return (
                <button
                  key={p}
                  type="button"
                  disabled={disabled}
                  title={disabled ? 'Purity is controlled by the Making Style — change the style at the top of Step 1' : ''}
                  data-testid={`step2-purity-${p}`}
                  onClick={() => {
                    if (disabled) {
                      toast({
                        title: 'Purity is locked on verified tags',
                        description: 'Change the Making Style at the top of Step 1 to switch purity.',
                      });
                      return;
                    }
                    const rate = todayPrices?.[p];
                    setForm(f => ({ ...f, goldPurity: p, goldRate: rate ? String(rate) : f.goldRate }));
                  }}
                  className={`px-4 py-3 rounded-lg text-base font-semibold border-2 transition-all ${
                    form.goldPurity === p ? 'bg-amber-500 text-white border-amber-500'
                    : disabled ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-amber-400'
                  }`}>
                  {p}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-base">Net Weight (grams)</Label>
          <Input value={calc.netWeight.toFixed(2)} readOnly className="bg-emerald-50 text-emerald-700 font-semibold text-base py-6" />
        </div>
        <div className="space-y-2">
          <Label className="text-base">Wastage %</Label>
          <Input type="number" step="0.1" inputMode="decimal" value={form.wastagePercentage}
            onChange={e => { if (isVerifiedFlow) setWastageOverridden(true); setForm(f => ({ ...f, wastagePercentage: e.target.value })); }}
            data-testid="step2-wastage-input"
            className="text-base py-6" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-base">Gold Rate (₹/gram) *</Label>
          <Input type="number" inputMode="numeric" value={form.goldRate} onChange={e => setForm(f => ({ ...f, goldRate: e.target.value }))} className="text-base py-6" data-testid="est-gold-rate" />
        </div>
        <div className="space-y-2">
          <Label className="text-base">Gold Amount</Label>
          <Input value={fmt(calc.goldAmount)} readOnly className="bg-emerald-50 text-emerald-700 font-bold text-base py-6" />
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-3">
      <p className="text-sm text-gray-500">Add stones below. Tap Next to skip if no stones.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Stone Name</Label>
          <Input value={currentStone.name} onChange={e => setCurrentStone(s => ({ ...s, name: e.target.value }))} className="text-base py-6" />
        </div>
        <div className="space-y-2">
          <Label>Weight (carats){editingStoneIndex != null && stones[editingStoneIndex]?._grouped ? ' — locked (from verified entries)' : ''}</Label>
          <div className="flex space-x-2">
            <Input type="number" step="0.01" inputMode="decimal" value={currentStone.weight}
              disabled={editingStoneIndex != null && stones[editingStoneIndex]?._grouped}
              data-testid="stone-weight-input"
              onChange={e => {
                const w = e.target.value;
                const autoAmt = w && currentStone.rate ? String(parseFloat(w) * parseFloat(currentStone.rate)) : '';
                setCurrentStone(s => ({ ...s, weight: w, amount: autoAmt }));
                setStoneAmountOverridden(false);
              }} className="text-base py-6 flex-1 disabled:bg-gray-100 disabled:text-gray-500" />
            {!(editingStoneIndex != null && stones[editingStoneIndex]?._grouped) && (
              <Button type="button" onClick={handleQuickAdd} className="bg-purple-500 hover:bg-purple-600 text-white whitespace-nowrap px-4">+ Add</Button>
            )}
          </div>
          {showWeightTotal && accumulatedWeights.length > 0 && (
            <div className="bg-purple-50 border border-purple-200 rounded p-2">
              <div className="flex items-center justify-between text-sm">
                <p className="text-purple-700 font-medium">{accumulatedWeights.join(' + ')} = {accumulatedWeights.reduce((s, w) => s + w, 0).toFixed(2)}ct</p>
                <div className="flex space-x-1">
                  <Button type="button" size="sm" onClick={handleUseTotal} className="bg-green-500 hover:bg-green-600 text-white text-xs">Use Total</Button>
                  <Button type="button" size="sm" variant="ghost" onClick={handleClearWeights} className="text-red-500 text-xs">Clear</Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Rate (₹/carat)</Label>
          <Input type="number" inputMode="numeric" value={currentStone.rate}
            onChange={e => {
              const r = e.target.value;
              const autoAmt = r && currentStone.weight ? String(parseFloat(currentStone.weight) * parseFloat(r)) : '';
              setCurrentStone(s => ({ ...s, rate: r, amount: autoAmt }));
              setStoneAmountOverridden(false);
            }} className="text-base py-6" />
        </div>
        <div className="space-y-2">
          <Label>Amount (₹){stoneCalcAmount > 0 && !stoneAmountOverridden ? ` — ${fmt(stoneCalcAmount)}` : ' - Override if needed'}</Label>
          <Input type="number" inputMode="numeric" value={stoneDisplayAmount}
            onChange={e => { setCurrentStone(s => ({ ...s, amount: e.target.value })); setStoneAmountOverridden(true); }}
            className={`text-base font-medium py-6 ${stoneAmountOverridden ? 'text-orange-600 border-orange-300' : 'text-purple-600'}`}
            placeholder={stoneCalcAmount > 0 ? fmt(stoneCalcAmount) : ''} />
        </div>
      </div>
      <Button type="button" onClick={handleAddStone} className="w-full bg-purple-500 hover:bg-purple-600 text-base py-6">
        <Plus className="w-5 h-5 mr-2" />{editingStoneIndex !== null ? 'Update Stone' : 'Add Stone'}
      </Button>
      {editingStoneIndex !== null && (
        <Button type="button" variant="outline" onClick={handleCancelEdit} className="w-full text-base py-6">Cancel Edit</Button>
      )}
      {stones.length > 0 && (
        <div className="space-y-2 mt-2">
          <p className="text-sm font-semibold text-purple-700">Added Stones ({stones.length}):</p>
          {stones.map((s, idx) => {
            const chipClass = s._labKind === 'certified'
              ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
              : s._labKind === 'in_house'
              ? 'bg-amber-50 text-amber-700 border-amber-200'
              : s._labKind === 'manual'
              ? 'bg-orange-50 text-orange-700 border-orange-200'
              : '';
            // A DV diamond that could not be grid-priced must NEVER show a
            // silent ₹0 — surface the actual reason and prompt manual setup.
            const needsSetup = s._kind === 'diamond'
              && s._pricing
              && s._pricing.rate_source !== 'grid';
            const setupReason = (s._pricing && s._pricing.reason) || 'no grid price';
            return (
            <div key={s.id || idx} onClick={() => handleEditStone(idx)}
              className={`flex items-center justify-between p-3 rounded-lg border-2 cursor-pointer transition-all ${editingStoneIndex === idx ? 'bg-purple-100 border-purple-500 shadow-md' : needsSetup ? 'bg-red-50 border-red-300 hover:border-red-400' : 'bg-purple-50 border-purple-200 hover:border-purple-400 hover:bg-purple-100'}`}
              data-testid={`estimate-stone-row-${idx}`}>
              <span className="text-base flex-1">
                <strong>{s.name || 'Stone'}</strong>
                {s._labChip && (
                  <span
                    className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${chipClass}`}
                    data-testid={`estimate-stone-lab-chip-${idx}`}
                    title={s._labKind === 'certified' ? 'External lab certified' : s._labKind === 'in_house' ? 'In-house TDS grade' : 'Manual rate — grid data unavailable'}
                  >
                    {s._labChip}{s._labKind === 'manual' ? ' · manual' : ''}
                  </span>
                )}
                {needsSetup ? (
                  <span
                    className="block text-xs text-red-600 font-semibold mt-1"
                    data-testid={`estimate-stone-needs-setup-${idx}`}
                  >
                    ⚠ Price unavailable — needs setup: {setupReason}. Tap to enter rate manually.
                  </span>
                ) : (
                  <span className="ml-1">
                    : {s.weight}ct{(s.pieces != null && s.pieces !== '') ? ` · ${s.pieces} pcs` : ''} @ {fmt(s.rate)}/ct = <strong className="text-purple-600">{fmt(s.amount)}</strong>
                  </span>
                )}
                {s._grouped && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setExpandedGroups(g => ({ ...g, [s.id]: !g[s.id] })); }}
                    className="block mt-1 text-[11px] font-semibold text-purple-600 underline decoration-dotted"
                    data-testid={`estimate-stone-group-toggle-${idx}`}
                  >
                    Contains {s._groupCount} entries {expandedGroups[s.id] ? '▲' : '▼'}
                  </button>
                )}
                {s._grouped && expandedGroups[s.id] && (
                  <div className="mt-1 pl-3 border-l-2 border-purple-200 space-y-0.5" data-testid={`estimate-stone-group-members-${idx}`}>
                    {s._members.map((m, mi) => (
                      <div key={mi} className="text-[11px] text-gray-500">
                        • {m.weight}ct{(m.pieces != null && m.pieces !== '') ? ` · ${m.pieces} pcs` : ''} @ {fmt(m.rate)}/ct = {fmt(m.amount)}
                      </div>
                    ))}
                  </div>
                )}
                {editingStoneIndex === idx && <span className="block text-xs text-purple-600 mt-1">Editing — update fields above</span>}
              </span>
              <Button type="button" size="sm" variant="ghost" onClick={e => { e.stopPropagation(); handleRemoveStone(idx); }} className="text-red-500 ml-2">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            );
          })}
          <div className="text-right text-base font-bold text-purple-700 bg-purple-100 px-4 py-2 rounded">
            Total Stones: {fmt(calc.stonesAmount)}
          </div>
        </div>
      )}
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-5">
      <div className="space-y-4">
        <div>
          <Label className="text-base font-semibold">Making Charges Type</Label>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-2">
            <label className="flex items-center space-x-2 cursor-pointer" data-testid="mc-radio-per-gram">
              <input type="radio" checked={form.makingChargesType === 'per_gram'} onChange={() => setForm(f => ({ ...f, makingChargesType: 'per_gram' }))} className="w-4 h-4 text-emerald-600" />
              <span className="text-sm">Per Gram</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer" data-testid="mc-radio-flat">
              <input type="radio" checked={form.makingChargesType === 'flat'} onChange={() => setForm(f => ({ ...f, makingChargesType: 'flat' }))} className="w-4 h-4 text-emerald-600" />
              <span className="text-sm">Flat <span className="text-[10px] text-gray-500">(small piece)</span></span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer" data-testid="mc-radio-fixed">
              <input type="radio" checked={form.makingChargesType === 'fixed'} onChange={() => setForm(f => ({ ...f, makingChargesType: 'fixed' }))} className="w-4 h-4 text-emerald-600" />
              <span className="text-sm">Fixed <span className="text-[10px] text-gray-500">(manual override)</span></span>
            </label>
          </div>
          {/* Context hint when the Making Style master picked this mode */}
          {form.makingChargesType === 'flat' && verifiedDvId && (
            <p className="text-[11px] text-amber-700 mt-1.5" data-testid="mc-flat-hint">
              Flat rate from Making Style — applies because the net weight is at or below the small-piece threshold.
            </p>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {form.makingChargesType === 'per_gram' ? (
            <div className="space-y-2"><Label className="text-base">Making Charges (₹/gram)</Label>
              <Input type="number" inputMode="numeric" value={form.makingChargesPerGram}
                onChange={e => { if (isVerifiedFlow) setMakingOverridden(true); setForm(f => ({ ...f, makingChargesPerGram: e.target.value })); }}
                className="text-base py-6" data-testid="mc-per-gram-input" /></div>
          ) : (
            <div className="space-y-2"><Label className="text-base">
              {form.makingChargesType === 'flat' ? 'Flat Making Charge (₹)' : 'Fixed Making Charge (₹)'}
            </Label>
              <Input type="number" inputMode="numeric" value={form.fixedMakingCharge}
                onChange={e => { if (isVerifiedFlow) setMakingOverridden(true); setForm(f => ({ ...f, fixedMakingCharge: e.target.value })); }}
                className="text-base py-6" data-testid="mc-flat-fixed-input" /></div>
          )}
          <div className="space-y-2"><Label className="text-base">Making Amount</Label>
            <Input value={fmt(calc.makingAmount)} readOnly className="bg-emerald-50 text-emerald-700 font-semibold text-base py-6" data-testid="mc-amount-readonly" /></div>
        </div>
      </div>
      <div className="space-y-2">
        <Label className="text-base">Other Charges (₹)</Label>
        <Input type="number" inputMode="numeric" value={form.otherCharges} onChange={e => setForm(f => ({ ...f, otherCharges: e.target.value }))} placeholder="For miscellaneous expenses" className="text-base py-6" />
      </div>
      <div className="border-t pt-4">
        <Label className="text-base font-semibold">Estimate Status</Label>
        <div className="flex items-center space-x-8 mt-3">
          <label className="flex items-center space-x-3 cursor-pointer"><input type="radio" checked={status === 'draft'} onChange={() => setStatus('draft')} className="w-5 h-5 text-emerald-600" /><span className="text-base">Draft</span></label>
          <label className="flex items-center space-x-3 cursor-pointer"><input type="radio" checked={status === 'confirmed'} onChange={() => setStatus('confirmed')} className="w-5 h-5 text-emerald-600" /><span className="text-base">Confirmed</span></label>
        </div>
      </div>
      <div className="space-y-3 border-t pt-4">
        <Label className="text-base">Photos (Optional)</Label>
        <div className="flex flex-wrap gap-2">
          {images.map((img, idx) => (
            <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200 bg-gray-100">
              <div className="absolute inset-0 flex items-center justify-center">
                <ImageOff className="w-6 h-6 text-gray-300" />
              </div>
              <img src={img.url} alt="" className="absolute inset-0 w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
              <button type="button" onClick={() => setImages(images.filter((_, i) => i !== idx))} className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs"><X className="w-3 h-3" /></button>
            </div>
          ))}
          {/* Camera capture button */}
          <button type="button" onClick={() => cameraInputRef.current?.click()} disabled={uploading}
            className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-400 hover:border-emerald-400 hover:text-emerald-500 transition-colors">
            {uploading ? <div className="w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /> : <><Camera className="w-5 h-5" /><span className="text-[10px] mt-0.5">Camera</span></>}
          </button>
          {/* Gallery picker button */}
          <button type="button" onClick={() => imageInputRef.current?.click()} disabled={uploading}
            className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-400 hover:border-emerald-400 hover:text-emerald-500 transition-colors">
            {uploading ? <div className="w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /> : <><Images className="w-5 h-5" /><span className="text-[10px] mt-0.5">Gallery</span></>}
          </button>
        </div>
        {/* Gallery input — multiple files */}
        <input ref={imageInputRef} type="file" accept="image/*" multiple className="hidden" onChange={async (e) => {
          const files = Array.from(e.target.files);
          if (!files.length) return;
          setUploading(true);
          try {
            const results = await Promise.all(files.map(f => fileService.uploadFile(f)));
            setImages(prev => [...prev, ...results.map((r, i) => ({ url: r.url, name: files[i].name }))]);
          } catch { toast({ title: 'Upload failed', variant: 'destructive' }); }
          finally { setUploading(false); e.target.value = ''; }
        }} />
        {/* Camera input — opens camera directly on Android */}
        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={async (e) => {
          const files = Array.from(e.target.files);
          if (!files.length) return;
          setUploading(true);
          try {
            const results = await Promise.all(files.map(f => fileService.uploadFile(f)));
            setImages(prev => [...prev, ...results.map((r, i) => ({ url: r.url, name: files[i].name }))]);
          } catch { toast({ title: 'Upload failed', variant: 'destructive' }); }
          finally { setUploading(false); e.target.value = ''; }
        }} />
      </div>
      <div className="border-t pt-4 bg-gray-50 rounded-lg p-4 space-y-2">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Breakdown</p>
        <div className="flex justify-between text-sm"><span className="text-gray-600">Gold</span><span className="font-medium">{fmt(calc.goldAmount)}</span></div>
        {calc.stonesAmount > 0 && <div className="flex justify-between text-sm"><span className="text-gray-600">Stones</span><span className="font-medium">{fmt(calc.stonesAmount)}</span></div>}
        <div className="flex justify-between text-sm"><span className="text-gray-600">Making</span><span className="font-medium">{fmt(calc.makingAmount)}</span></div>
        {parseFloat(form.otherCharges) > 0 && <div className="flex justify-between text-sm"><span className="text-gray-600">Other</span><span className="font-medium">{fmt(parseFloat(form.otherCharges))}</span></div>}
        <div className="flex justify-between text-sm border-t pt-2 mt-1"><span className="text-gray-600">GST (3%)</span><span className="font-medium">{fmt(calc.gstAmount)}</span></div>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(val) => { onOpenChange(val); if (!val) setWizardStep(1); }}>
      <DialogContent className="fixed bottom-0 left-0 right-0 top-auto translate-x-0 translate-y-0 max-w-full w-full h-[92vh] flex flex-col p-0 gap-0 overflow-hidden rounded-t-2xl [&>button]:hidden border-0 shadow-xl" data-testid="estimate-form-dialog">
        {/* Accessible labels — visually hidden but read by screen readers. */}
        <DialogTitle className="sr-only">{stepTitles[wizardStep - 1] || 'New Estimate'}</DialogTitle>
        <DialogDescription className="sr-only">
          Step {wizardStep} of {stepTitles.length}. Fill in the estimate details across customer, item, stones, making charges and photos.
        </DialogDescription>
        {/* Sticky Wizard Header */}
        <div className="flex-shrink-0 px-4 pt-4 pb-3 border-b bg-white">
          <div className="flex items-center mb-2.5 gap-2">
            <div className="w-16 flex-shrink-0 flex items-center">
              {wizardStep > 1 && (
                <button type="button" onClick={() => setWizardStep(s => s - 1)}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-colors" data-testid="wizard-back-btn">
                  <ChevronLeft className="w-6 h-6 text-gray-600" />
                </button>
              )}
            </div>
            <p className="flex-1 text-base font-bold text-gray-900 text-center">{stepTitles[wizardStep - 1]}</p>
            <div className="w-16 flex-shrink-0 flex items-center justify-end gap-1.5">
              {wizardStep === 4 ? (
                <button type="button" onClick={handleSave} disabled={saving}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold px-3 py-2 rounded-lg flex items-center gap-1 disabled:opacity-60 min-h-[40px]" data-testid="save-estimate-btn">
                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : null}Save
                </button>
              ) : (
                <button type="button" onClick={handleWizardNext}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 transition-colors shadow-sm" data-testid="wizard-next-btn">
                  <ChevronRight className="w-6 h-6 text-white" />
                </button>
              )}
              <button type="button" onClick={() => onOpenChange(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors" data-testid="wizard-close-btn">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
          </div>
          {/* Summary cards */}
          <div className="grid grid-cols-3 gap-1">
            <div className="bg-gray-50 rounded-lg px-2.5 py-2"><p className="text-[9px] text-gray-400 font-medium leading-none mb-1">Net Weight</p><p className="text-sm font-bold text-gray-900 leading-none">{calc.netWeight.toFixed(2)}<span className="text-xs font-medium text-gray-500 ml-0.5">g</span></p></div>
            <div className="bg-amber-50 rounded-lg px-2.5 py-2"><p className="text-[9px] text-gray-400 font-medium leading-none mb-1">Gold</p><p className="text-sm font-bold text-amber-700 leading-none">{fmt(calc.goldAmount)}</p></div>
            <div className="bg-purple-50 rounded-lg px-2.5 py-2"><p className="text-[9px] text-gray-400 font-medium leading-none mb-1">Stones</p><p className="text-sm font-bold text-purple-600 leading-none">{fmt(calc.stonesAmount)}</p></div>
            <div className="bg-blue-50 rounded-lg px-2.5 py-2"><p className="text-[9px] text-gray-400 font-medium leading-none mb-1">MC Amount</p><p className="text-sm font-bold text-blue-600 leading-none">{fmt(calc.makingAmount)}</p></div>
            <div className="bg-orange-50 rounded-lg px-2.5 py-2"><p className="text-[9px] text-gray-400 font-medium leading-none mb-1">GST (3%)</p><p className="text-sm font-bold text-orange-500 leading-none">{fmt(calc.gstAmount)}</p></div>
            <div className="bg-emerald-500 rounded-lg px-2.5 py-2"><p className="text-[9px] text-emerald-100 font-medium leading-none mb-1">Total</p><p className="text-sm font-bold text-white leading-none">{fmt(calc.totalAmount)}</p></div>
          </div>
        </div>
        {/* Step Content */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          {wizardStep === 1 && renderStep1()}
          {wizardStep === 2 && renderStep2()}
          {wizardStep === 3 && renderStep3()}
          {wizardStep === 4 && renderStep4()}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EstimateFormDialog;
